import { useEffect, useRef } from "react";

/*
  Aurora borealis — WebGL fragment shader.
  Layered noise + flowing bands, blended into the sky at low opacity.
*/

const VERT = `
attribute vec2 position;
void main() { gl_Position = vec4(position, 0.0, 1.0); }
`;

// Aurora shader — physical-feeling curtains.
//   • Serpentine ribbon centerlines (multi-harmonic sines) instead of blob noise
//   • Multiple stacked curtains at different altitudes drift independently
//   • Domain warp adds organic curl
//   • Vertical high-freq striations mimic the light-ray curtain look
//   • Natural aurora colour ramp: emerald low → gold mid → faint magenta top
//   • Slow fbm fade so whole regions dim & re-emerge
const FRAG = `
precision highp float;
uniform float u_time;
uniform vec2 u_resolution;

float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);} 
float noise(vec2 p){
  vec2 i=floor(p), f=fract(p);
  vec2 u=f*f*(3.0-2.0*f);
  return mix(mix(hash(i),hash(i+vec2(1.0,0.0)),u.x),
             mix(hash(i+vec2(0.0,1.0)),hash(i+vec2(1.0,1.0)),u.x),u.y);
}
float fbm(vec2 p){
  float v=0.0, a=0.5;
  for(int i=0;i<6;i++){ v+=a*noise(p); p=p*2.03+vec2(3.7,1.3); a*=0.5; }
  return v;
}

// Exponential ribbon around a serpentine centerline. Each ribbon breathes
// (slowly fades in/out on its own phase) so nothing has a hard start/end.
float ribbon(vec2 uv, float y0, float amp, float freq, float thick, float phase, float breatheOff){
  float wave = amp        * sin(uv.x*freq        + phase)
             + amp*0.55   * sin(uv.x*freq*2.13   + phase*1.3 + 1.7)
             + amp*0.30   * sin(uv.x*freq*4.10   + phase*0.7 + 3.1);
  float d = uv.y - y0 - wave;
  float breathe = 0.30 + 0.70 * (0.5 + 0.5 * sin(u_time * 0.14 + breatheOff));
  return exp(-abs(d)/max(thick, 0.001)) * breathe;
}

void main(){
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  float t = u_time;

  // Domain warp — subtle organic curl of the whole field
  vec2 w;
  w.x = fbm(uv*3.0 + vec2(t*0.09, 0.0));
  w.y = fbm(uv*3.0 + vec2(0.0, t*0.09) + 7.3);
  vec2 wuv = uv + (w - 0.5) * vec2(0.22, 0.09);

  // Main curtains — moved DOWN (lower y0), overlap wordmark area
  float r1 = ribbon(wuv, 0.42, 0.065, 4.5, 0.090, t*0.24,        0.3);
  float r2 = ribbon(wuv, 0.30, 0.055, 6.3, 0.055, t*0.18 + 2.1,  1.9);
  float r3 = ribbon(wuv, 0.54, 0.080, 3.2, 0.100, t*0.15 + 4.4,  3.7);
  float r4 = ribbon(wuv, 0.20, 0.045, 8.1, 0.045, t*0.28 + 1.3,  5.2);

  // Shimmery low wisps — very thin, fade behind the tree line
  float w1 = ribbon(wuv, 0.10, 0.038,  9.0, 0.028, t*0.14 + 0.4, 2.6);
  float w2 = ribbon(wuv, 0.05, 0.028, 12.0, 0.022, t*0.20 + 2.9, 4.9);
  float w3 = ribbon(wuv, 0.15, 0.042,  7.5, 0.030, t*0.12 + 4.7, 0.7);

  float mainBand = max(max(r1, r2), max(r3, r4));
  float wispBand = max(w1, max(w2, w3));
  float band = max(mainBand, wispBand * 0.75);

  // Vertical light-ray striations — softer near horizon so wisps blur out
  float striation = 0.55 + 0.75 * fbm(vec2(uv.x*22.0 + t*0.30, uv.y*3.2));
  striation *= 0.55 + 0.45 * smoothstep(0.10, 0.65, uv.y);

  // Shimmer — vertical light-ray fibres, slow gentle pulse
  // (high-freq in x = vertical stripes; low-freq in y = long rays)
  float shimmer = fbm(vec2(uv.x*44.0 + t*0.18, uv.y*2.0 + t*0.10));
  shimmer = 0.65 + 0.55 * shimmer;

  // Sparkle — rare vertical pinpoint flickers, slow twinkle
  float sparkleN = noise(vec2(uv.x*95.0 + t*0.35, uv.y*4.0 - t*0.20));
  float sparkle = pow(sparkleN, 8.0) * 3.0;

  // Global slow fade — entire zones dim & re-emerge
  float slowFade = smoothstep(0.15, 0.85, fbm(uv*1.15 + t*0.045));

  // Vertical envelope — very soft bottom fade so wisps bleed behind trees
  // with no discernible start line. Top also feathered to nothing.
  float vMask = smoothstep(-0.05, 0.14, uv.y) * (1.0 - smoothstep(0.72, 1.02, uv.y));

  float glow = band * striation * shimmer * vMask * slowFade;

  // Natural aurora palette (gold + green forward, magenta only high & faint)
  vec3 emerald = vec3(0.10, 0.85, 0.42);
  vec3 lime    = vec3(0.55, 0.98, 0.50);
  vec3 gold    = vec3(1.00, 0.82, 0.32);
  vec3 amber   = vec3(0.98, 0.62, 0.20);
  vec3 magenta = vec3(0.82, 0.28, 0.58);

  // Vertical colour gradient
  float vpos = clamp((uv.y - 0.20) / 0.70, 0.0, 1.0);
  vec3 col = mix(emerald, lime, smoothstep(0.0, 0.30, vpos));
  col      = mix(col, gold,    smoothstep(0.38, 0.78, vpos));
  col      = mix(col, magenta, smoothstep(0.88, 1.05, vpos) * 0.55);

  // Horizontal gold patches — natural interchange, not uniform
  float goldPatch = smoothstep(0.46, 0.74, fbm(uv * vec2(1.4, 0.4) + t*0.04));
  col = mix(col, mix(gold, amber, 0.4), goldPatch * 0.55);

  // Bright core along ribbon spines — warmer where gold, greener elsewhere
  float core = pow(band, 3.0) * vMask;
  col += mix(vec3(0.15, 0.42, 0.20), vec3(0.55, 0.42, 0.15), goldPatch) * core * 0.9;

  // Pinpoint sparkle flickers on the ribbon core — pure hot light
  col += vec3(1.0, 0.92, 0.65) * sparkle * core * slowFade;

  vec3 finalColor = col * glow * 1.55;
  gl_FragColor = vec4(finalColor, glow * 0.95);
}
`;

const compileShader = (gl, type, src) => {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(shader));
  }
  return shader;
};

const AuroraShader = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl", { premultipliedAlpha: true, antialias: true });
    if (!gl) return;

    const program = gl.createProgram();
    gl.attachShader(program, compileShader(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(program, compileShader(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(program);
    gl.useProgram(program);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );
    const posLoc = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const timeLoc = gl.getUniformLocation(program, "u_time");
    const resLoc = gl.getUniformLocation(program, "u_resolution");

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const pw = Math.round(w * dpr);
      const ph = Math.round(h * dpr);
      if (canvas.width !== pw || canvas.height !== ph) {
        canvas.width = pw;
        canvas.height = ph;
        gl.viewport(0, 0, pw, ph);
      }
      gl.uniform2f(resLoc, pw, ph);
    };
    resize();
    window.addEventListener("resize", resize);

    const render = (t) => {
      gl.uniform1f(timeLoc, t * 0.001);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div
      aria-hidden
      className="absolute inset-x-0 top-0 pointer-events-none"
      style={{
        height: "82%",
        zIndex: 4,
        mixBlendMode: "screen",
        opacity: 0.62,
      }}
      data-testid="aurora"
    >
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
};

export default AuroraShader;
